export const NAVICA = {
  name: "Navica Supports",
  legal_name: "Navica Supports Pty Ltd",
  tagline: "Navigating Care with YOU.",
  seal: "Always with you, never above you.",
  aasw_number: "691718",
  director: "Madelyn Agius",
  director_title: "Director · Support Coordinator · AASW Social Worker",
  email: "info@navicasupports.com",
  director_email: "madelyn@navicasupports.com",
  phone: "1300 105 034",
  phoneRaw: "+611300105034",
  address: "19/103 George Street, Parramatta NSW 2150",
  address_short: "Parramatta, NSW",
  city: "Sydney, Australia",
  abn: "43 693 656 905",
  ndis_provider_status: "Application in progress",
  ndis_provider_number: "TBC",
  social: {
    facebook: "https://www.facebook.com/navicasupports",
    instagram: "https://www.instagram.com/navicasupports",
  },
};

// Welcome video. Drop in a YouTube or Vimeo URL when the Auslan recording is ready.
// Leave videoUrl as null to show the "coming soon" state.
export const WELCOME_VIDEO = {
  videoUrl: null, // e.g. "https://www.youtube.com/embed/VIDEO_ID" (with captions/Auslan)
  posterImage: "https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?auto=format&fit=crop&w=1400&q=80",
  duration: "Approx. 90 seconds",
  hasAuslan: true,
  hasCaptions: true,
  transcript: [
    "Welcome to Navica Supports.",
    "We are an AASW aligned social work practice and NDIS support coordination service, here in Sydney.",
    "Our work is simple to say, slow to do well. We sit with people, listen carefully, and help map a path forward that fits the life you actually want to live.",
    "Whether you are new to the NDIS, supporting a young person, navigating recovery, or stepping into the spaces of DVA, family violence, or athlete wellbeing, you are welcome here.",
    "This video is presented with Auslan and English captions. Communication should never be the barrier.",
    "Reach out when you are ready. We will pick up the phone, read your email, and walk the next step with you.",
  ],
};

export const NAV_ITEMS = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  { label: "Services", path: "/services" },
  { label: "Events", path: "/events" },
  { label: "FAQ", path: "/faq" },
  { label: "Connect", path: "/contact" },
];

export const DEPARTMENTS = [
  {
    slug: "ndis",
    name: "NDIS",
    tagline: "Support that helps you get more from your plan.",
    color: "coral",
    services: [
      {
        title: "Support Coordination (Level 2)",
        short: "Coordination of Supports. Your plan in plain language, your goals at the centre.",
        points: [
          "Coordination of Supports (COS Level 2)",
          "Building capacity in daily supports and life admin",
          "Provider matching, comparing and reviewing",
          "Reporting to your Plan Manager or NDIA",
          "Navigating complex or crisis situations calmly",
          "Advocacy at every step, in your voice",
        ],
      },
      {
        title: "Psychosocial Recovery Coaching (PSRC)",
        short: "Recovery focused, longer term support for people living with psychosocial disability.",
        points: [
          "Rebuilding routine, purpose and wellbeing",
          "Working with mental health teams and allied health",
          "Support during hospital, crisis and step down",
          "Skill building for independent living",
          "Family and carer engagement where you want it",
        ],
      },
      {
        title: "Early Childhood Supports",
        short: "Family centred coordination for children under 9.",
        points: [
          "Family centred coordination for children under 9",
          "Linking with allied health and early intervention",
          "School and early learning integration",
          "Building capacity for parents and carers",
          "Smooth transitions into mainstream NDIS supports",
        ],
      },
      {
        title: "Employment Assistance",
        short: "SLES and 1:1 support to find and hold meaningful work.",
        points: [
          "School Leaver Employment Supports (SLES)",
          "1:1 employment assistance and job coaching",
          "Career planning, résumé writing and interview prep",
          "Workplace induction and on the job supports",
          "Linking with mainstream employment providers and DES",
        ],
      },
    ],
  },
  {
    slug: "social-work",
    name: "Social Work",
    tagline: "AASW aligned social work across the parts of life that need holding steady.",
    color: "navy",
    services: [
      {
        title: "Social Work & Advocacy",
        short: "Foundational social work practice underpinning every engagement across the department.",
        points: [
          "Psychosocial assessment",
          "Social work assessment",
          "Counselling and therapeutic social work",
          "Advocacy",
          "NDIS reports and supporting evidence",
          "Case conferencing",
          "Housing advocacy",
          "Hospital and health system navigation",
          "Family support",
          "Justice system navigation",
          "Complex case management",
          "Capacity building",
        ],
      },
      {
        title: "Private Social Work",
        short: "Confidential, fee for service social work for people, families and organisations outside statutory or NDIS funding streams.",
        points: [
          "Life transitions, grief, identity and relationships",
          "Family and parenting support",
          "Workplace, medico-legal and third party reports",
          "Flexible scheduling and telehealth options Australia wide",
          "Confidential, judgement free space",
        ],
      },
      {
        title: "DVA (Department of Veterans' Affairs)",
        short: "Trauma informed social work for veterans and their families, funded through DVA.",
        points: [
          "Coordination of DVA funded supports",
          "Transition from service to civilian life",
          "Partner and family engagement",
          "Linking to ex service organisations, allied health and community",
          "Support with identity, purpose and life after service",
        ],
      },
      {
        title: "OOHC (Out of Home Care)",
        short: "Wrap around social work for young people, kin and carer households navigating OOHC arrangements.",
        points: [
          "Support for children, young people and carers",
          "Kin, foster and residential arrangements",
          "Trauma informed, culturally safe practice",
          "Working alongside DCJ caseworkers",
          "Education, health and community engagement",
        ],
      },
      {
        title: "Prison, Custody & DCJ",
        short: "Reintegration and social work for people in or leaving custody, and those working with the Department of Communities and Justice.",
        points: [
          "In custody visits and pre release planning",
          "Reintegration after release: housing, income, ID, community",
          "Court navigation support (we are not lawyers, we are social workers alongside your legal team)",
          "Family reconnection and safety planning",
          "Coordination with corrections, DCJ, health and community services",
        ],
      },
      {
        title: "Juvenile Justice",
        short: "Advocacy and social work for young people in the youth justice system, and the families around them.",
        points: [
          "Support for young people in Youth Justice centres and on community orders",
          "Family, school and community reintegration",
          "Diversion and rehabilitation planning",
          "Coordination with Youth Justice, DCJ, courts and schools",
          "Strengths based, non judgemental practice",
        ],
      },
    ],
  },
  {
    slug: "mind-and-motion",
    name: "Mind & Motion",
    tagline: "Athlete mental health, movement, community and awareness.",
    color: "coral",
    services: [
      {
        title: "Weekly community sessions",
        short: "All abilities. All ages. Together. Moving forward. Across Oran Park, Penrith, Campbelltown and Blacktown.",
        points: [
          "Inclusive weekly movement sessions",
          "All abilities, all ages welcome",
          "Delivered in Oran Park, Penrith, Campbelltown and Blacktown",
          "Focus on connection, confidence and wellbeing",
          "Partnership with Poly|Build",
        ],
      },
      {
        title: "Awareness events",
        short: "Community facing events that shift the conversation around athlete wellbeing.",
        points: [
          "Signature event: Protecting the Nine",
          "Panels, workshops and community days",
          "Lived experience storytelling",
          "Partnerships with clubs, codes and community groups",
        ],
      },
      {
        title: "Performance social work",
        short: "1:1 mental health, identity and return to play support for athletes at every level.",
        points: [
          "Mental health support during injury and rehabilitation",
          "Identity, purpose and transition coaching",
          "Return to play and performance mindset work",
          "Liaison with coaches, clubs and medical teams",
          "Confidential, athlete led sessions",
        ],
      },
    ],
  },
];

export const SERVICES = [
  {
    slug: "support-coordination",
    title: "NDIS Support Coordination",
    subtitle: "AASW led",
    short: "Your plan in clear language, your goals at the centre, your supports actually talking to each other.",
    icon: "Compass",
    color: "coral",
    points: [
      "Understanding your NDIS plan in clear, everyday language",
      "Setting and achieving personal goals",
      "Connecting with the right local services",
      "Coordinating providers and service agreements",
      "Plan reviews and life transitions",
      "Building independence and confidence",
    ],
    available: true,
  },
  {
    slug: "psychosocial-recovery-coaching",
    title: "Psychosocial Recovery Coaching",
    subtitle: "NDIS PSRC",
    short: "Strengths based, hope led coaching for people living with psychosocial disability.",
    icon: "HeartHandshake",
    color: "coral",
    points: [
      "Recovery oriented planning",
      "Crisis prevention and wellbeing strategies",
      "Reconnecting with community and purpose",
      "Coordinated support across providers",
      "Lived experience informed practice",
    ],
    available: true,
  },
  {
    slug: "early-childhood-approach",
    title: "Early Childhood Approach Supports",
    subtitle: "NDIS · Ages 0 to 9",
    short: "Family centred coordination for young children. Warm, patient, grounded in what your family needs.",
    icon: "Sparkles",
    color: "orange",
    points: [
      "Family centred coordination for children under 9",
      "Linking with allied health and early intervention",
      "School and early learning integration",
      "Building capacity for parents and carers",
      "Smooth transitions into mainstream NDIS supports",
    ],
    available: true,
  },
  {
    slug: "employment-assistance",
    title: "Employment Assistance",
    subtitle: "NDIS · SLES & 1:1",
    short: "Practical, person-led employment support for NDIS participants, from finding purpose to holding a job you love.",
    icon: "Briefcase",
    color: "navy",
    points: [
      "School Leaver Employment Supports (SLES)",
      "1:1 employment assistance and job coaching",
      "Career planning, résumé writing and interview prep",
      "Workplace induction and on-the-job supports",
      "Linking with mainstream employment providers and DES",
    ],
    available: true,
  },
  {
    slug: "dva-social-work",
    title: "DVA Social Work & Wellbeing Coordination",
    subtitle: "Veterans and families",
    short: "Walking alongside veterans and their families. Trauma informed, respectful of service.",
    icon: "Award",
    color: "navy",
    points: [
      "Navigating DVA claims and entitlements",
      "Connecting with veteran specific services",
      "Family and carer support",
      "Mental health and transition coaching",
    ],
    available: false,
    badge: "Coming soon",
  },
  {
    slug: "dfv-social-work",
    title: "Domestic & Family Violence Social Work",
    subtitle: "Trauma informed · AASW aligned",
    short: "Confidential social work support for people impacted by family and intimate partner violence.",
    icon: "ShieldCheck",
    color: "coral",
    points: [
      "Safety planning and risk assessment",
      "Trauma informed counselling support",
      "Court and legal system navigation",
      "Connecting with refuge and crisis services",
    ],
    available: false,
    badge: "Coming soon",
  },
  {
    slug: "mind-and-motion",
    title: "Mind & Motion",
    subtitle: "Navica × Poly|Build · Built Different",
    short: "Athlete mental health, performance and recovery, holistic social work meets movement. All abilities. All ages. Together. Moving forward.",
    icon: "Activity",
    color: "orange",
    points: [
      "Mental health support during injury, rehabilitation and life transitions",
      "Identity, purpose and return-to-play coaching",
      "Inclusive practice, everyone is welcome, respected and valued",
      "Understanding, we embrace diverse abilities and support individual learning and growth",
      "Opportunities, meaningful spaces to move, connect and succeed",
      "Delivered across Oran Park, Penrith, Campbelltown and Blacktown",
    ],
    available: true,
  },
];

export const ASSISTANCE = [
  { title: "Understanding Your NDIS Plan", items: [
    "We help you understand your NDIS plan, funding, and goals",
    "We explain information in clear, easy to understand language",
    "We ensure you know what supports are available to you",
  ]},
  { title: "Connecting You With the Right Supports", items: [
    "We work with you to identify providers that suit your needs",
    "We connect you with providers who align with your preferences and goals",
    "We ensure the supports you access are the right fit for you",
  ]},
  { title: "Coordinating Your Supports", items: [
    "We coordinate services to ensure they work together effectively",
    "We help reduce stress by managing interactions between supports",
    "We ensure your supports continue to run smoothly and efficiently",
  ]},
  { title: "Advocacy & Problem Solving", items: [
    "We advocate for your needs to ensure your voice is heard",
    "We assist with resolving issues that arise with supports or services",
    "We support clear communication with providers and the NDIA",
  ]},
  { title: "Building Capacity & Independence", items: [
    "We support you to build confidence in managing your supports",
    "We help you develop skills to take a more active role in your NDIS journey",
    "We empower you to make decisions and take control of achieving your goals",
  ]},
  { title: "Plan Reviews & Life Transitions", items: [
    "We assist with NDIS plan reviews to ensure your supports remain relevant",
    "We support you through changes in circumstances that affect your plan",
    "We help ensure your services continue to meet your evolving needs",
  ]},
];

export const NDIS_RESOURCES = [
  { title: "NDIS Official Website", url: "https://www.ndis.gov.au", desc: "The official portal for the National Disability Insurance Scheme." },
  { title: "NDIS Quality & Safeguards Commission", url: "https://www.ndiscommission.gov.au", desc: "Independent regulator for NDIS providers and workers." },
  { title: "AASW Find a Social Worker", url: "https://www.aasw.asn.au", desc: "Australian Association of Social Workers, directory and ethics." },
  { title: "1800RESPECT", url: "https://www.1800respect.org.au", desc: "National domestic, family and sexual violence counselling service." },
  { title: "Open Arms, Veterans & Families Counselling", url: "https://www.openarms.gov.au", desc: "Free, confidential support for veterans and their families." },
  { title: "Lifeline Australia", url: "https://www.lifeline.org.au", desc: "24/7 crisis support and suicide prevention. Call 13 11 14." },
  { title: "Beyond Blue", url: "https://www.beyondblue.org.au", desc: "Mental health information, advice and support." },
  { title: "Carer Gateway", url: "https://www.carergateway.gov.au", desc: "Supports, services and respite for unpaid carers." },
];

// Anonymised placeholder testimonials. User can edit and replace with real consent backed quotes.
export const TESTIMONIALS = [
  {
    id: "t-k-participant",
    quote: "Navica didn't just explain my plan, they sat with me until it made sense. For the first time I felt like I had someone in my corner who actually got it.",
    author: "K., NDIS Participant",
    role: "Support Coordination · Western Sydney",
    rating: 5,
  },
  {
    id: "t-family-ec",
    quote: "As a parent navigating early childhood supports for the first time, I was overwhelmed. The team was patient, warm and brilliant at connecting us with the right therapists.",
    author: "Family member",
    role: "Early Childhood Support",
    rating: 5,
  },
  {
    id: "t-m-planmanaged",
    quote: "What I appreciate most is the honesty. If something isn't working, they tell me. If a provider isn't a good fit, they help me find another. No spin.",
    author: "M., Plan managed participant",
    role: "Support Coordination",
    rating: 5,
  },
  {
    id: "t-allied-partner",
    quote: "We refer participants to Navica with confidence. Communication is fast, documentation is clear, and outcomes are real.",
    author: "Allied Health Partner",
    role: "Referrer · Sydney metro",
    rating: 5,
  },
  {
    id: "t-j-recovery",
    quote: "After years of feeling like a file number, I finally feel like a person again. They helped me reconnect with community and rebuild routines I thought I'd lost.",
    author: "J., Psychosocial Recovery participant",
    role: "Recovery Coaching",
    rating: 5,
  },
  {
    id: "t-family-review",
    quote: "The plan review process was seamless. They prepared me, advocated for what I needed, and the outcome reflected my goals, not the system's defaults.",
    author: "Family carer",
    role: "Plan Review Support",
    rating: 5,
  },
];

export const PARTNERS = [
  { category: "Allied Health", items: ["Occupational Therapy clinics", "Speech Pathology providers", "Physiotherapy networks", "Psychology and counselling practices", "Exercise Physiology"] },
  { category: "Plan Management", items: ["Registered Plan Managers across NSW", "Self managed support guidance"] },
  { category: "Healthcare", items: ["GP clinics and medical centres", "Community mental health teams", "Hospital social work departments"] },
  { category: "Community & Advocacy", items: ["Disability advocacy organisations", "Cultural and multicultural services", "LGBTIQA+ inclusive services", "Aboriginal and Torres Strait Islander community orgs"] },
  { category: "Housing & SDA", items: ["SDA and SIL providers", "Tenancy support services", "Homelessness response partners"] },
  { category: "Specialist Networks", items: ["Veterans' support organisations", "Family violence services", "Sporting clubs and rehab facilities"] },
];

export const CREDENTIALS = [
  { title: "AASW", desc: "Australian Association of Social Workers. Accredited social work practice.", icon: "Award" },
  { title: "Bachelor of Social Work", desc: "Tertiary qualified and ethics bound practice.", icon: "GraduationCap" },
  { title: "NDIS Worker Screening Check", desc: "Current clearance held by all client facing staff.", icon: "ShieldCheck" },
  { title: "Working with Children Check", desc: "Current WWCC for all staff supporting under 18s.", icon: "Shield" },
  { title: "Professional Indemnity Insurance", desc: "Comprehensive cover for social work practice.", icon: "FileCheck2" },
  { title: "Public Liability Insurance", desc: "Held by Navica Supports Pty Ltd.", icon: "Briefcase" },
  { title: "NDIS Code of Conduct", desc: "Practice aligned with NDIS Quality and Safeguards Commission standards.", icon: "BookCheck" },
  { title: "AASW Code of Ethics", desc: "Bound by the AASW Code of Ethics and supervision standards.", icon: "ScrollText" },
];

export const CULTURAL_SAFETY = {
  intro: "Cultural safety isn't a tick box. It's how we practise. Below are the concrete commitments we hold ourselves to.",
  pillars: [
    {
      title: "Aboriginal & Torres Strait Islander participants",
      points: [
        "We listen to community led practice frameworks and respect kinship structures",
        "We connect with Aboriginal Community Controlled Health Organisations where appropriate",
        "We never assume. We ask, and we adjust",
        "We honour Sorry Business and culturally significant time",
      ],
    },
    {
      title: "Culturally & linguistically diverse communities",
      points: [
        "We work with TIS National (the free interpreting service) at no cost to participants",
        "We can match participants with gender aligned workers on request",
        "Documents can be requested in clear, simple language or with interpreter support",
        "We respect religious practice, dietary needs and community protocols",
      ],
    },
    {
      title: "LGBTIQA+ participants & families",
      points: [
        "We use the names, pronouns and identifying language each participant chooses",
        "We seek out providers with explicit LGBTIQA+ inclusion practices",
        "We do not require participants to disclose identity to receive services",
      ],
    },
    {
      title: "Trauma informed practice",
      points: [
        "We are guided by trauma informed frameworks across all interactions",
        "Participants control pace, place and presence in every conversation",
        "We do not require people to re tell their stories to multiple workers",
      ],
    },
    {
      title: "Disability led practice",
      points: [
        "Nothing about you without you. Participants lead every plan",
        "We use clear language, Easy Read on request, and accessible meeting formats",
        "We respect the social model of disability and lived expertise",
      ],
    },
  ],
};

// Curated stock imagery: community focused, warm and friendly.
// Anonymous groups, hands meeting, gatherings, not staff portraits.
// To be swapped with the client's own real community event photos when shared.
export const IMAGERY = {
  // Hero, supplied by Navica: community gathering with diverse participants, person in wheelchair, bunting and outdoor stalls
  heroHands: "https://customer-assets.emergentagent.com/job_social-care-hub-6/artifacts/87ha58bf_ChatGPT%20Image%20Jun%2030%2C%202026%2C%2012_25_22%20PM.png",
  // Backup hero, friends arm around shoulders, golden hour
  heroAlt: "https://images.unsplash.com/photo-1529070538774-1843cb3265df?auto=format&fit=crop&w=1400&q=80",
  // Community gathering, circle of people from above
  community: "https://images.unsplash.com/photo-1582731595062-04b5fa3c8f72?auto=format&fit=crop&w=1400&q=80",
  // Hands meeting in the centre, universal "we're in this together" gesture
  handsCircle: "https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1400&q=80",
  // Quiet planning moment, open journal in golden light
  notebook: "https://images.unsplash.com/photo-1455390582262-044cdead277a?auto=format&fit=crop&w=1400&q=80",
  // Community cafe / shared meal vibe
  coffee: "https://images.unsplash.com/photo-1517457373958-b7bdd4587205?auto=format&fit=crop&w=1400&q=80",
  // Community gathering space (chairs in a circle, warm light)
  communitySpace: "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?auto=format&fit=crop&w=1400&q=80",
  // Group walking together outdoors (from behind)
  walking: "https://images.unsplash.com/photo-1531545514256-b1400bc00f31?auto=format&fit=crop&w=1400&q=80",
  // Outdoor community / park
  garden: "https://images.unsplash.com/photo-1530908295418-a12e326966ba?auto=format&fit=crop&w=1400&q=80",
  // Plant by window
  plant: "https://images.unsplash.com/photo-1485955900006-10f4d324d411?auto=format&fit=crop&w=1400&q=80",
};
